import * as React from "react";
import * as _ from "lodash";
import {translate} from "react-i18next";

// services
import {AssignedGroupRoleDto, GroupDto, RoleDto,} from "../../../services/Client";

// reducer
import {ITranslatedComponent} from "../../../redux/reducers/generalReducer";

// semantic
import {Modal, Table} from "semantic-ui-react";

import Button from "../../../components/Button";
import Textfield from "../../../components/TextField";
import AssignedRoleSelection, {RoleAssignmentType} from "./AssignedRoleSelection";

export interface IGroupCreationDialogProps extends ITranslatedComponent {
  /** All existing groups. Used to check for duplicates. */
  groups: GroupDto[];

  /** All available roles. Possible roles for the new group. */
  roles: RoleDto[];

  /** Callback when the user closes the dialog. */
  onClose?: (newGroup?: GroupDto) => any;
}

interface IGroupCreationDialogState {
  /** The name of the new group. */
  name: string;

  /** The set of roles of the new group. */
  assignedRoles: AssignedGroupRoleDto[];
}

/**
 * The GroupCreationDialog collects all necessary information for
 * creating a new group. The component doesn't create the group itself
 * but passes a GroupDto to it's client through onClose() which is
 * expected to call the back end API to create the group.
 */
export class GroupCreationDialog extends React.Component<IGroupCreationDialogProps, IGroupCreationDialogState> {
  readonly dummyGroupForAssignedRole: GroupDto = {
    groupName: "",
    objectId: 0,
    versionId: 0
  };

  constructor(props: IGroupCreationDialogProps) {
    super(props);

    this.state = {
      name: "",
      assignedRoles: [],
    };
  }

  validateInput = (existingGroups: GroupDto[], existingRoles: RoleDto[]): boolean => {
    const containsGroup = (name: string, groups: GroupDto[]): boolean => {
      return _.find(groups, group => group.groupName.toLowerCase() === this.state.name.toLowerCase()) !== undefined;
    };

    // TODO: check the name for proper format&characters!
    const nameOk = Boolean(this.state.name) && !containsGroup(this.state.name, existingGroups);
    const rolesOk = this.state.assignedRoles.length !== 0;
    return nameOk && rolesOk;
  }

  onClickCreate = (): void => {
    if (this.props.onClose) {
      // Each AssignedGroupRoleDto requires a reference of the group it is asigned
      // to. But when creating a new group we don't have that DTO. In this case
      // the back end is satisfied with an empty DTO.
      const newGroup: GroupDto = {
        groupName: this.state.name,
        assignedRoles: this.state.assignedRoles,
        objectId: 0, // dummy objectId for creation
        versionId: 0, // dummy versionId for creation
      };
      this.props.onClose(newGroup);
    }
  }

  onClickCancel = (): void => {
    if (this.props.onClose) {
      this.props.onClose();
    }
  }

  renderNameControl = (): JSX.Element => (
    <Textfield
      placeholder={this.props.t("ENTER_GROUP_NAME")}
      onChange={(event: React.FormEvent<{}>, newValue: string) => {
        this.setState({ name: newValue });
      }}
    />
  )

  renderRolesControl = (): JSX.Element => (
    <AssignedRoleSelection
      roles={this.props.roles}
      roleAssignType={RoleAssignmentType.GROUP}
      parentDto={this.dummyGroupForAssignedRole}
      values={this.state.assignedRoles}
      onChange={(assignedRoles: AssignedGroupRoleDto[]) => {
        this.dummyGroupForAssignedRole.assignedRoles = assignedRoles;
        this.setState({assignedRoles: assignedRoles});
      }}
    />
  )

  renderContent = (existingGroups: GroupDto[], existingRoles: RoleDto[]): JSX.Element => (
    <>
    <Table definition={true}>
      <Table.Body>
        <Table.Row>
          <Table.Cell>{this.props.t("GROUP_NAME")}*</Table.Cell>
          <Table.Cell>{this.renderNameControl()}</Table.Cell>
        </Table.Row>
        <Table.Row>
          <Table.Cell>{this.props.t("ROLES")}*</Table.Cell>
          <Table.Cell>{this.renderRolesControl()}</Table.Cell>
        </Table.Row>
      </Table.Body>
    </Table>
    * {this.props.t("MANDATORY_FIELD")}
    </>
  )

  render() {
    const { groups, roles } = this.props;
    return (
      <Modal open={true} style={{"marginTop": 0}}>
        <Modal.Header>{this.props.t("ADD_GROUP")}</Modal.Header>
        <Modal.Content>
          {this.renderContent(groups, roles)}
        </Modal.Content>
        <Modal.Actions>
          <Button
            icon={"checkmark"}
            disabled={!this.validateInput(groups, roles)}
            onClick={this.onClickCreate}
          />
          <Button
            icon={"ban"}
            onClick={this.onClickCancel}
          />
        </Modal.Actions>
      </Modal>
    );
  }
}

export default translate("ADMIN_VIEW")(GroupCreationDialog);



// WEBPACK FOOTER //
// ./src/views/AdminView/AccessTab/GroupCreationDialog.tsx